<script setup lang="ts">
import FaIcon from '@/application/components/ui/FaIcon.vue';
import { ElAlert } from 'element-plus';
import { ElCheckbox, ElCheckboxGroup } from 'element-plus';
import { useVirtualList } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { storeToRefs } from "pinia";
import { useUserDataStore } from "@/user/store/userDataStore";
import type { BankAccount } from "@/api/bankAccount/types";
import { useContractStore } from "@/contracts/stores/contractStore";
import type { ContractOption } from "@/contracts/stores/types";

interface Props {
  modelValue: number[],
  bankAccountId?: number,
}

const props = defineProps<Props>();
const emit = defineEmits(['close', 'update:modelValue'])

const { contractOptionsOwnContracts } = storeToRefs(useContractStore());
const { contractToBankAccountMap } = storeToRefs(useUserDataStore());

const checkedContractIds = ref<number[]>(props.modelValue);
const foreignAssignedContractIsChecked = ref(false);

function getAssignedBankAccount(contractId: number): BankAccount|null {
  return contractToBankAccountMap.value.get(contractId) ?? null;
}

function isAssignedToThis(contractId: number): boolean {
  return !!props.bankAccountId && getAssignedBankAccount(contractId)?.id === props.bankAccountId;
}

function isAssignedToOther(contractId: number): boolean {
  const account = getAssignedBankAccount(contractId);

  return !!account && account.id !== props.bankAccountId;
}

function handleSelectAll(value: boolean): void {
  checkedContractIds.value = value ? contractOptionsOwnContracts.value.map(contract => contract.value) : [];
}

watch(checkedContractIds, (ids) => {
  emit('update:modelValue', ids);
  foreignAssignedContractIsChecked.value = ids.some(isAssignedToOther);
});

function bankAccountInfo(contractId: number): string {
  const account = getAssignedBankAccount(contractId);
  if (!account) return '-';

  return `${account.bankName} ${account.iban}`
}

// The row height is set on both the list and the item: `useVirtualList` uses it to
// compute scroll height, so the item has to match it.
const ITEM_HEIGHT = 70;

const sortedContractOptions = computed(() => [...contractOptionsOwnContracts.value].sort(
  (a: ContractOption, b: ContractOption) => {
    const aAssignedToThis = isAssignedToThis(a.value);
    const aAssignedToOther = isAssignedToOther(a.value);
    const bAssignedToThis = isAssignedToThis(b.value);
    const bAssignedToOther = isAssignedToOther(b.value);

    if (aAssignedToThis && !bAssignedToThis) return -1;
    if (bAssignedToThis && !aAssignedToThis) return 1;
    if (aAssignedToOther && !bAssignedToOther) return -1;
    if (bAssignedToOther && !aAssignedToOther) return 1;

    // Wenn beide gleich zugewiesen sind, alphabetische Reihenfolge
    return a.label.localeCompare(b.label, 'de');
  }
));

const { list, containerProps, wrapperProps } = useVirtualList(sortedContractOptions, { itemHeight: ITEM_HEIGHT });
</script>

<template>
  <div class="flex flex-col gap-4 justify-between">
    <div
      class="rounded-full px-2 py-1 hover:bg-gray-100 self-start -ml-1 mt-1 cursor-pointer"
      @click="emit('close')"
    >
      <FaIcon
        name="arrow-left-long"
        size="lg"
      />
      <span class="ml-2">zurück</span>
    </div>
    <Transition name="fade" mode="out-in">
      <ElAlert
        v-if="foreignAssignedContractIsChecked"
        type="info"
        title="Zugeordnete Verträge"
        class="mb-2 text-start el-alert--primary"
        show-icon
      >
        Bestehende Zuordnungen zu anderen Bankverbindungen werden überschrieben
        und die Abbuchung der Beiträge wird umgestellt.
      </ElAlert>
    </Transition>
    <div class="flex flex-col gap-5 items-start p-1 border rounded-lg w-full">
      <ElCheckbox
        id="select-all"
        :model-value="checkedContractIds.length > 0"
        :indeterminate="checkedContractIds.length > 0 && checkedContractIds.length !== contractOptionsOwnContracts.length"
        :label="`Alle auswählen (${checkedContractIds.length} von ${contractOptionsOwnContracts.length} ausgewählt)`"
        class="p-1"
        @update:model-value="handleSelectAll"
      />
      <div
        v-bind="containerProps"
        class="w-full max-h-80 md:max-h-96"
      >
        <ElCheckboxGroup
          v-model="checkedContractIds"
          v-bind="wrapperProps"
          class="space-y-1"
        >
          <div
            v-for="{ data: contract } in list"
            :key="contract.value"
            class="flex"
            :style="{ height: `${ITEM_HEIGHT}px` }"
          >
            <ElCheckbox
              :value="contract.value"
              class="el-checkbox--multiline w-full text-start"
            >
              <div class="flex flex-col text-gray-900 text-xs">
                <span class="text-sm w-11/12 truncate">{{ contract.label }} {{ contract.inactive ? '(inaktiv)' : '' }}</span>
                <span>{{ contract.number ? `Nr.: ${contract.number}` : 'Keine Vertragsnummer' }}</span>
                <span
                  v-if="isAssignedToOther(contract.value)"
                  class="text-orange-800 bg-orange-100 px-1.5 py-0.5 rounded-md mr-auto"
                >
                  zugeordnet zu: {{ bankAccountInfo(contract.value) }}
                </span>
                <span
                  v-else-if="isAssignedToThis(contract.value)"
                  class="text-green-800 bg-green-100 px-1.5 py-0.5 rounded-md mr-auto"
                >
                  dieser Bankverbindung zugeordnet
                </span>
                <span
                  v-else
                  class="text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded-md mr-auto"
                >
                  Noch nicht zugeordnet
                </span>
              </div>
            </ElCheckbox>
          </div>
        </ElCheckboxGroup>
      </div>
    </div>
  </div>
</template>
